import { useEffect, useState } from 'react';
import axios from 'axios';
import { PageShell } from '../components/PageShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const extractYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

const WatchPage = () => {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/videos`).then(r => {
      setVideos(r.data);
      if (r.data.length > 0) setActive(r.data[0]);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const activeYtId = active ? extractYouTubeId(active.video_url) : null;

  return (
    <PageShell>
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <p className="font-heading text-[10px] tracking-[0.4em] uppercase text-primary-main/80 mb-3">Live &amp; On-Demand</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white">Watch RDPT</h1>

        {loading ? (
          <p className="mt-12 text-white/40">Loading…</p>
        ) : videos.length === 0 ? (
          <div className="mt-12 text-center py-16 border border-white/[0.04] rounded-2xl">
            <p className="font-body text-sm text-white/40">No videos yet. Streams coming soon.</p>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Player */}
            <div className="lg:col-span-2">
              {activeYtId ? (
                <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/[0.08]" data-testid="watch-player">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${activeYtId}`}
                    title={active.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="aspect-video rounded-2xl border border-white/[0.06] bg-white/[0.02] flex items-center justify-center">
                  <p className="text-white/40">Invalid video URL</p>
                </div>
              )}
              {active && (
                <div className="mt-4">
                  <h2 className="font-heading text-xl sm:text-2xl font-bold text-white" data-testid="watch-active-title">
                    {active.is_live && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] uppercase tracking-widest mr-3 align-middle">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
                      </span>
                    )}
                    {active.title}
                  </h2>
                  {active.description && <p className="mt-2 font-body text-sm text-white/55 leading-relaxed">{active.description}</p>}
                </div>
              )}
            </div>

            {/* Playlist */}
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
              {videos.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setActive(v)}
                  data-testid={`watch-thumb-${v.id}`}
                  className={`group w-full text-left flex gap-3 rounded-xl border p-2 transition-colors ${
                    active?.id === v.id ? 'border-primary-main/40 bg-white/[0.04]' : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'
                  }`}
                >
                  <div className="flex-shrink-0 w-28 aspect-video rounded-md overflow-hidden bg-black">
                    {v.thumbnail_url ? (
                      <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary-main/10 to-secondary-main/10" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-heading text-sm font-bold text-white line-clamp-2 group-hover:text-primary-main transition-colors">{v.title}</h4>
                    <p className="mt-1 font-mono text-[10px] text-white/30 uppercase tracking-wider">{new Date(v.published_at).toLocaleDateString()}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
    </PageShell>
  );
};

export default WatchPage;
